import { Icon } from '@eventbrite/eds-icon';
import { CalendarChunky } from '@eventbrite/eds-iconography';
import {
    EventbriteEvent,
    getEventDatesFormat,
    getFormattedEventDate as getFormattedEventDateStandardized,
    TimezoneLocale,
} from '@eventbrite/event-date-time';
import { gettext } from '@eventbrite/i18n';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import type {
    ChildEventProps,
    DateAndTimeScopedProps,
    Event,
} from '../../../../types';
import { useIsServerSideRendered } from '../../../hooks/useIsServerSideRendered';
import { getUTCTimezone } from '../../../utils/localizedDates';
import {
    Detail,
    DetailContent,
    DetailHeading,
    DetailIcon,
    ICON_SIZE,
} from '../../../_shared/Detail/Detail';
import { LoadingSkeleton } from '../../../_shared/LoadingSkeleton/LoadingSkeleton';
import { EventDetailsMainInner } from '../../EventDetails';
import { ChildEventsDropdown } from './ChildEventsDropdown';
import { ChildEventsSlider } from './ChildEventsSlider';
import './DateAndTime.scss';
import { DoorTime } from './DoorTime';

export type DateAndTimeProps = DateAndTimeScopedProps & {
    isRepeatingChildEventsSliderExperiment: boolean;
    locale: string;
    enableDatetimeStandardization?: boolean;
    event: Pick<
        Event,
        | 'id'
        | 'start'
        | 'end'
        | 'hideStartDate'
        | 'hideEndDate'
        | 'childEvents'
        | 'isOnlineEvent'
    > & {
        listingDisplayFlags: {
            shouldShowTimezone: boolean;
        };
    };
};

const formattedEventDateStandardized = ({
    event,
    locale,
    isOnlineEvent,
    isServerSideRendered,
}: {
    event: EventbriteEvent;
    locale: string;
    isOnlineEvent: boolean;
    isServerSideRendered: boolean;
}) => {
    const getTimezoneFrom =
        isOnlineEvent && !isServerSideRendered
            ? TimezoneLocale.Attendee
            : TimezoneLocale.Creator;

    return getFormattedEventDateStandardized({
        event,
        locale,
        datesFormat: getEventDatesFormat({
            event,
            locale,
            useTimezoneFrom: getTimezoneFrom,
        }),
        useTimezoneFrom: getTimezoneFrom,
    });
};

export const DateAndTime = ({
    event,
    displayDate,
    displayEndDateWithTz,
    displayStartDateWithTz,
    dateLineTwo,
    dateLineOne,
    isSameStartEndDate,
    shouldShowDoorTime,
    doorTime,
    doorTimeLineOne,
    doorTimeLineTwo,
    doorTimeLineThree,
    isRepeatingChildEventsSliderExperiment,
    locale,
    enableDatetimeStandardization,
}: DateAndTimeProps) => {
    const {
        id: eventId,
        childEvents,
        isOnlineEvent,
        hideEndDate,
        hideStartDate,
    } = event;

    const [timezone, setTimezone] = useState('');
    const [isLoadingUserLocalization, setIsLoadingUserLocalization] =
        useState(isOnlineEvent);
    const [formattedDateStandardized, setFormattedDateStandardized] = useState(
        () => {
            if (enableDatetimeStandardization) {
                return formattedEventDateStandardized({
                    event,
                    locale,
                    isOnlineEvent,
                    isServerSideRendered,
                });
            } else {
                return '';
            }
        },
    );

    const { isServerSideRendered } = useIsServerSideRendered();

    useEffect(() => {
        const timezone = getUTCTimezone({
            date: event.start,
            isOnlineEvent,
            locale,
            isRepeatingChildEventsSliderExperiment,
        });
        timezone && setTimezone(timezone);

        if (isOnlineEvent && enableDatetimeStandardization) {
            setFormattedDateStandardized(
                formattedEventDateStandardized({
                    event,
                    locale,
                    isOnlineEvent,
                    isServerSideRendered,
                }),
            );
            setIsLoadingUserLocalization(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isServerSideRendered]);

    if (!displayDate) {
        return null;
    }

    const LABEL_ID = 'date-time-heading';
    let separator = '';

    const EventDateTime = () => {
        const shouldShowDoorTimeLineTwo =
            (!isSameStartEndDate && !hideEndDate) || hideStartDate;
        const shouldRenderComma =
            isSameStartEndDate && !hideEndDate && !hideStartDate && !doorTime;
        const shouldRenderSpace = !isSameStartEndDate;
        if (shouldRenderComma) {
            separator = ', ';
        } else if (shouldRenderSpace) {
            separator = ' ';
        }

        const MusicEventDateTime = () => {
            const doorsAtLine = doorTimeLineThree && `, ${doorTimeLineThree}`;

            return (
                <>
                    {doorTimeLineOne}
                    {separator}
                    {shouldShowDoorTimeLineTwo ? (
                        <span className="date-and-time__line-break">
                            {shouldShowDoorTimeLineTwo ? doorTimeLineTwo : ''}
                            {doorsAtLine}
                        </span>
                    ) : (
                        <>{doorsAtLine}</>
                    )}
                </>
            );
        };
        const DefaultEventDateTime = () => (
            <>
                {dateLineOne}
                {separator}
                {shouldShowDoorTimeLineTwo ? (
                    <span className="date-and-time__line-break">
                        {dateLineTwo}
                    </span>
                ) : (
                    <>{dateLineTwo}</>
                )}
            </>
        );

        return shouldShowDoorTime ? (
            <MusicEventDateTime />
        ) : (
            <DefaultEventDateTime />
        );
    };

    const DateTimeLinesStandardized = () => {
        const dateTime = shouldShowDoorTime ? (
            <>
                {formattedDateStandardized}.{' '}
                <DoorTime
                    dateAndTimezone={{
                        utc: doorTime as string,
                        timezone: event.start.timezone,
                    }}
                    locale={locale}
                    isOnlineEvent={isOnlineEvent}
                />
            </>
        ) : (
            formattedDateStandardized
        );

        const dateTimeClasses = classNames({
            'eds-is-hidden-accessible': isLoadingUserLocalization,
        });

        return (
            <p>
                {isLoadingUserLocalization && (
                    <LoadingSkeleton width="100%" borderRadius="4px" />
                )}
                <span className={dateTimeClasses}>{dateTime}</span>
            </p>
        );
    };

    const DateTimeLines = () => (
        <>
            {enableDatetimeStandardization ? (
                <DateTimeLinesStandardized />
            ) : (
                <p className="date-and-time">
                    <meta
                        content={displayStartDateWithTz}
                        data-testid="event-start-date-tz"
                    />
                    <meta
                        content={displayEndDateWithTz}
                        data-testid="event-end-date-tz"
                    />
                    <time>
                        <EventDateTime />
                    </time>
                </p>
            )}
        </>
    );

    const shouldShowChildEvents = childEvents && childEvents.length > 0;

    return (
        <>
            <section aria-labelledby={LABEL_ID}>
                <EventDetailsMainInner>
                    <Detail>
                        <DetailIcon>
                            <Icon
                                type={<CalendarChunky />}
                                size={ICON_SIZE}
                                isBlockLevel={true}
                            />
                        </DetailIcon>
                        <DetailContent>
                            <DetailHeading>
                                <h3 id={LABEL_ID}>
                                    {gettext('Date and time')}
                                </h3>
                            </DetailHeading>
                            <DateTimeLines />
                            {shouldShowChildEvents ? (
                                <>
                                    {isRepeatingChildEventsSliderExperiment ? (
                                        <p>
                                            {timezone &&
                                                `All in ${timezone} timezone`}
                                        </p>
                                    ) : (
                                        <ChildEventsDropdown
                                            childEvents={
                                                childEvents as Array<ChildEventProps>
                                            }
                                            eventId={eventId}
                                            isOnlineEvent={isOnlineEvent}
                                        />
                                    )}
                                </>
                            ) : null}
                        </DetailContent>
                    </Detail>
                </EventDetailsMainInner>
                {shouldShowChildEvents &&
                isRepeatingChildEventsSliderExperiment ? (
                    <ChildEventsSlider
                        childEvents={childEvents as Array<ChildEventProps>}
                        eventId={eventId}
                        isOnlineEvent={isOnlineEvent}
                    />
                ) : null}
            </section>
        </>
    );
};
